import { useEffect, useState } from 'react';

interface SubmitGameButtonProps {
  /** Whether the submission window is open (from the DB challenge state). */
  open: boolean;
  /** Submission deadline (ISO string) for the active edition, if any. */
  deadlineIso: string | null;
  /** IANA timezone used to render the closed-state deadline label. */
  timezone?: string;
  /** Starter repo to fork — where "submit your game" leads when open. */
  href?: string;
}

function formatCountdown(deadline: Date, now: Date) {
  const diff = deadline.getTime() - now.getTime();
  if (diff <= 0) {
    return '0D 0H 0M 0S';
  }
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / (60 * 60 * 24));
  const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;
  return `${days}D ${hours}H ${minutes}M ${seconds}S`;
}

function formatDeadline(deadline: Date, timezone?: string) {
  return new Intl.DateTimeFormat('es', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone,
  }).format(deadline);
}

export function SubmitGameButton({
  open,
  deadlineIso,
  timezone,
  href,
}: SubmitGameButtonProps) {
  const deadline = deadlineIso ? new Date(deadlineIso) : null;

  const [countdown, setCountdown] = useState(() =>
    deadline ? formatCountdown(deadline, new Date()) : '',
  );

  useEffect(() => {
    if (!open || !href || !deadlineIso) {
      return;
    }
    const target = new Date(deadlineIso);
    const update = () => setCountdown(formatCountdown(target, new Date()));
    update();
    const intervalId = window.setInterval(update, 1_000);
    return () => window.clearInterval(intervalId);
  }, [open, href, deadlineIso]);

  if (!open || !href || !deadline) {
    return (
      <div
        aria-disabled
        className="inline-flex min-w-40 cursor-not-allowed flex-col items-start gap-1 border border-muted-foreground/30 bg-muted px-4 py-3 text-left text-muted-foreground shadow-[4px_4px_0px_rgba(0,0,0,0.25)]"
      >
        <span className="font-sans text-sm uppercase tracking-[0.16em]">
          Sube tu juego
        </span>
        <span className="font-mono text-[11px] uppercase tracking-[0.08em]">
          {deadline
            ? `Cerrado ${formatDeadline(deadline, timezone)}`
            : 'Cerrado'}
        </span>
      </div>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="hover:-translate-y-0.5 inline-flex min-w-40 items-center justify-between gap-3 border border-primary/35 bg-primary px-4 py-3 text-left text-background shadow-[4px_4px_0px_rgba(0,0,0,0.45),0_0_0_0_rgba(217,255,0,0.32)] transition-all duration-200 ease-out [animation:arcadeSubmitPulse_1.8s_ease-in-out_infinite] hover:translate-x-0.5 hover:scale-[1.02] hover:shadow-[2px_2px_0px_rgba(0,0,0,0.55),0_0_0_8px_rgba(217,255,0,0.14)]"
    >
      <span className="font-sans text-sm uppercase tracking-[0.16em]">
        Sube tu juego
      </span>
      <span className="font-mono text-[11px] text-background/75 uppercase tracking-[0.08em]">
        {countdown}
      </span>
    </a>
  );
}
